import { apiClient } from './client'
import type {
  RequestItem,
  RequestItemStatus,
  RequestItemPriority,
  ProcurementResult,
  SourceType,
  StatusHistoryEntry,
} from './types'
import type { TableRow } from '../../modules/table/types'

interface TableItemRaw extends RequestItem {
  /** Считает сервер по общему правилу закупки (см. api processed-rule.ts):
   *  своя/командная квота с ценой либо финальный per-user state. */
  isProcessed?: boolean
  request: {
    requestNumber: string | null
    deadline: string | null
    createdAt: string
    managerId: string
    status: string
    manager: { id: string; email: string; name?: string | null } | null
    client: { id: string; name: string; category: string; code: string | null }
  }
}

export interface TableItemsResponse {
  data: TableItemRaw[]
  total: number
  page: number
}

function mapToTableRow(item: TableItemRaw): TableRow {
  return {
    id: item.id,
    clientName: item.request?.client?.name ?? '',
    requestId: item.request?.requestNumber ?? item.requestId,
    code: item.request?.client?.code ?? '',
    clientCategory: item.request?.client?.category ?? '',
    date: item.request?.createdAt?.split('T')[0] ?? '',
    partNumber: item.partNumber,
    brand: item.brand ?? '',
    qty: item.quantity,
    unit: item.unit ?? '',
    totalY: null,
    soprovod: item.request?.manager?.name || item.request?.manager?.email || '',
    priority: item.priority,
    deadline: item.request?.deadline?.split('T')[0] ?? '',
    comments: item.noteForProcurement ?? '',
    targetPrice: item.targetPrice,
    status: item.status,
    isProcessed: item.isProcessed ?? false,
  }
}

export interface TableFilters {
  search?: string
  status?: string
  clientCategory?: string
  priority?: string
}

export async function getTableItems(
  page = 1,
  limit = 500,
  filters: TableFilters = {},
): Promise<{ rows: TableRow[]; total: number; page: number }> {
  const params: Record<string, unknown> = { page, limit }
  if (filters.search) params['search'] = filters.search
  if (filters.status) params['status'] = filters.status
  if (filters.clientCategory) params['clientCategory'] = filters.clientCategory
  if (filters.priority) params['priority'] = filters.priority
  const res = await apiClient.get<TableItemsResponse>(`/request-items`, { params })
  return {
    rows: res.data.data.map(mapToTableRow),
    total: res.data.total,
    page: res.data.page,
  }
}

export async function getItemsByRequest(requestId: string): Promise<RequestItem[]> {
  const res = await apiClient.get<{ success: true; data: RequestItem[] }>(
    `/requests/${requestId}/items`,
  )
  return res.data.data
}

export async function getItem(id: string): Promise<RequestItem> {
  const res = await apiClient.get<{ success: true; data: RequestItem }>(`/request-items/${id}`)
  return res.data.data
}

export async function createItem(
  requestId: string,
  data: {
    partNumber: string
    brand?: string | null
    quantity: number
    unit?: string | null
    priority?: RequestItemPriority
    targetPrice?: number | null
    noteForProcurement?: string | null
    matrix?: string | null
    comment?: string | null
    datasheetUrl?: string | null
  },
): Promise<RequestItem> {
  const res = await apiClient.post<{ success: true; data: RequestItem }>(
    `/requests/${requestId}/items`,
    data,
  )
  return res.data.data
}

// Bulk-создание при импорте: один HTTP вместо N.
// Лимит 1000 позиций; битые (partNumber='???' / quantity=0 / пустой бренд)
// попадают в БД и подсвечиваются красным в UI.
export async function createItemsBulk(
  requestId: string,
  items: Array<{
    partNumber: string
    customerPartNumber?: string | null
    brand?: string | null
    quantity: number
    unit?: string | null
    priority?: RequestItemPriority
    targetPrice?: number | null
    noteForProcurement?: string | null
    matrix?: string | null
    category?: string | null
    comment?: string | null
  }>,
): Promise<RequestItem[]> {
  const res = await apiClient.post<{ success: true; data: RequestItem[] }>(
    `/requests/${requestId}/items/bulk`,
    { items },
  )
  return res.data.data
}

/**
 * Заменить все позиции черновика одной транзакцией (форма «Новая заявка»):
 * сервер удаляет прежние и создаёт новые разом — либо новый набор целиком, либо
 * прежний. Пустой список допустим. У оформленной заявки — 409.
 */
export async function replaceDraftItems(
  requestId: string,
  items: Parameters<typeof createItemsBulk>[1],
): Promise<RequestItem[]> {
  const res = await apiClient.put<{ success: true; data: RequestItem[] }>(
    `/requests/${requestId}/draft-items`,
    { items },
  )
  return res.data.data
}

export async function patchItem(
  id: string,
  fields: Partial<{
    partNumber: string
    brand: string | null
    quantity: number
    unit: string | null
    priority: string
    targetPrice: number | null
    priceStock: number | null
    priceManufacturer: number | null
    priceRF: number | null
    priceOther: number | null
    comment: string | null
    noteForProcurement: string | null
    kpNote: string | null
    assignedToId: string | null
    status: RequestItemStatus
    matrix: string | null
    category: string | null
    /** Ссылка на даташит: только http(s), null — снять. */
    datasheetUrl: string | null
  }>,
): Promise<RequestItem> {
  const res = await apiClient.patch<{ success: true; data: RequestItem }>(
    `/request-items/${id}`,
    fields,
  )
  return res.data.data
}

/**
 * Создаёт/обновляет «Цену менеджера» — синтетическую квоту с ценой, которую
 * менеджер вбил прямо в колонку матрицы в таблице заявки. Парная операция с
 * patchItem: когда поле позиции меняется, автоквота с этой же ценой
 * появляется в procurement_results — тогда чекбокс на странице позиции
 * автоматически встаёт напротив этой квоты (через useEffect сравнения цен).
 */
export async function upsertManagerPrice(
  itemId: string,
  payload: {
    sourceType: 'STOCK' | 'CHINA' | 'RF' | 'MANUFACTURER' | 'OTHER'
    price: number
    currency: 'RUB' | 'USD' | 'EUR' | 'CNY'
  },
): Promise<void> {
  await apiClient.put(`/request-items/${itemId}/manager-price`, payload)
}

export async function updateItemStatus(
  id: string,
  status: RequestItemStatus,
  note?: string,
): Promise<RequestItem> {
  const res = await apiClient.patch<{ success: true; data: RequestItem }>(`/request-items/${id}`, {
    status,
    note,
  })
  return res.data.data
}

/** Массовая смена статуса позиций одним запросом — bulk-эквивалент N одиночных
 *  updateItemStatus. Позиции могут быть из разных заявок (очередь закупки).
 *  Заменяет залп Promise.allSettled(updateItemStatus), пробивавший rate-limit
 *  (инцидент 04.06.2026). Бэк сам отфильтровывает позиции с уже целевым статусом. */
export async function bulkUpdateItemStatus(
  ids: string[],
  status: RequestItemStatus,
): Promise<{ updated: number }> {
  const res = await apiClient.patch<{ success: true; data: { updated: number } }>(
    `/request-items/bulk-status`,
    { ids, status },
  )
  return res.data.data
}

export async function assignToMe(id: string): Promise<RequestItem> {
  const res = await apiClient.post<{ success: true; data: RequestItem }>(
    `/request-items/${id}/assign-to-me`,
  )
  return res.data.data
}

export async function sendNeedsInfo(id: string, note: string): Promise<RequestItem> {
  const res = await apiClient.post<{ success: true; data: RequestItem }>(
    `/request-items/${id}/needs-info`,
    { note },
  )
  return res.data.data
}

export async function sendToRework(id: string, note: string): Promise<RequestItem> {
  const res = await apiClient.post<{ success: true; data: RequestItem }>(
    `/request-items/${id}/rework`,
    { note },
  )
  return res.data.data
}

export async function getItemHistory(id: string): Promise<StatusHistoryEntry[]> {
  const res = await apiClient.get<{ success: true; data: StatusHistoryEntry[] }>(
    `/request-items/${id}/history`,
  )
  return res.data.data
}

export async function getProcurementResults(itemId: string): Promise<ProcurementResult[]> {
  const res = await apiClient.get<{ success: true; data: ProcurementResult[] }>(
    `/request-items/${itemId}/procurement-results`,
  )
  return res.data.data
}

export async function deleteItem(id: string): Promise<{ deleted: number }> {
  const res = await apiClient.delete<{ success: true; data: { deleted: number } }>(
    `/request-items/${id}`,
  )
  return res.data.data
}

export async function deleteItems(ids: string[]): Promise<{ deleted: number }> {
  const res = await apiClient.delete<{ success: true; data: { deleted: number } }>(
    `/request-items/bulk`,
    { data: { ids } },
  )
  return res.data.data
}

/** Текущий закупщицкий state для текущего пользователя (PROC/ROS).
 *  teamState — только у РОЗ: агрегат его команды закупки. */
export async function getProcurementState(
  itemId: string,
): Promise<{ state: string; teamState?: string }> {
  const res = await apiClient.get<{ success: true; data: { state: string; teamState?: string } }>(
    `/request-items/${itemId}/procurement-state`,
  )
  return res.data.data
}

/** Ручной закупщицкий state позиции: WAITING / NEEDS_INFO / NO_RESPONSE / REFUSED.
 *  Автопереходы (QUOTED / REQUESTED) через этот эндпоинт не проходят. */
export async function setProcurementState(
  itemId: string,
  state: string,
  note?: string | null,
): Promise<{ state: string }> {
  const res = await apiClient.patch<{ success: true; data: { state: string } }>(
    `/request-items/${itemId}/procurement-state`,
    { state, note: note ?? null },
  )
  return res.data.data
}

/** Массово проставить матрицу для позиций в заявке.
 *  matrix=null — «снять» матрицу. */
export async function bulkUpdateMatrix(
  requestId: string,
  itemIds: string[],
  matrix: string | null,
): Promise<{ updated: number }> {
  const res = await apiClient.patch<{ success: true; data: { updated: number } }>(
    `/requests/${requestId}/items/bulk-matrix`,
    { itemIds, matrix },
  )
  return res.data.data
}

/** Массово назначить позиции закупщику (видимость в очереди) или снять
 *  назначение (assignedToId=null → «показать всем»). Назначаются только
 *  позиции с матрицей Китай/Производители — остальные приходят в skipped. */
/** Конфликт назначения: по позиции открыт запрос поставщика другого закупщика. */
export interface AssignConflict {
  requestItemId: string
  partNumber: string
  byName: string
  byRole: string
  fileCode: string
  supplierName: string
}

export interface BulkAssignResult {
  updated: number
  skipped: number
  /** Есть живая работа других закупщиков — назначение НЕ выполнено,
   *  нужен повтор с force=true после подтверждения. */
  conflicts?: AssignConflict[]
}

export async function bulkAssignProcurement(
  requestId: string,
  itemIds: string[],
  assignedToId: string | null,
  force = false,
): Promise<BulkAssignResult> {
  const res = await apiClient.patch<{ success: true; data: BulkAssignResult }>(
    `/requests/${requestId}/items/bulk-assign`,
    { itemIds, assignedToId, force },
  )
  return res.data.data
}

export async function bulkUpdateDealStatus(
  requestId: string,
  payload: {
    itemIds: string[]
    status: 'ORDERED' | 'REJECTED_BY_CUSTOMER'
    quotationId?: string
    reason?: string
  },
): Promise<{
  updated: number
  requestStatusChanged: boolean
  newRequestStatus: string | null
  /** Заказ, оформленный отметкой «Счёт» (12.10.2026). */
  orderId?: string | null
  orderCreated?: boolean
  /** Отметка сохранена, а заказ не оформился — причина (аудит 20.10.2026). */
  orderWarning?: string | null
}> {
  const res = await apiClient.patch<{
    success: true
    data: {
      updated: number
      requestStatusChanged: boolean
      newRequestStatus: string | null
      orderId?: string | null
      orderCreated?: boolean
      orderWarning?: string | null
    }
  }>(`/requests/${requestId}/items/bulk-deal-status`, payload)
  return res.data.data
}

// Снять статус сделки (В заказ / Отказ клиента) у позиций — возврат «без статуса».
export async function bulkClearDealStatus(
  requestId: string,
  // Без quotationId снимается любой финальный статус — в т.ч. у «осиротевших»
  // позиций, чьё КП-источник удалено (кнопка в модалке оформления сделки).
  payload: { itemIds: string[]; quotationId?: string },
): Promise<{
  updated: number
  requestStatusChanged: boolean
  newRequestStatus: string | null
  /** Заказ удалён: позиций «Счёт» не осталось, оплат не было. */
  orderDeleted?: boolean
  /** Заказ с оплатами оставлен как есть — показать менеджеру. */
  orderWarning?: string | null
}> {
  const res = await apiClient.patch<{
    success: true
    data: {
      updated: number
      requestStatusChanged: boolean
      newRequestStatus: string | null
      orderDeleted?: boolean
      orderWarning?: string | null
    }
  }>(`/requests/${requestId}/items/bulk-clear-deal-status`, payload)
  return res.data.data
}

export async function updateProcurementResult(
  id: string,
  data: Partial<{
    supplierName: string
    price: number
    currency: string
    sourceType: SourceType
    note: string | null
  }>,
): Promise<ProcurementResult> {
  const res = await apiClient.patch<{ success: true; data: ProcurementResult }>(
    `/procurement-results/${id}`,
    data,
  )
  return res.data.data
}

export async function deleteProcurementResult(id: string): Promise<void> {
  await apiClient.delete(`/procurement-results/${id}`)
}

export async function createProcurementResult(
  itemId: string,
  data: {
    supplierName: string
    price: number
    currency?: string
    stockQty?: number
    leadTimeDays?: number
    moq?: number
    dateCode?: string
    conditionType?: string
    sourceType: SourceType
    note?: string
  },
): Promise<ProcurementResult> {
  const res = await apiClient.post<{ success: true; data: ProcurementResult }>(
    `/request-items/${itemId}/procurement-results`,
    data,
  )
  return res.data.data
}

// Bulk-импорт ответа поставщика: один HTTP + один WS-broadcast.
// Лимит 500 квот за вызов. Порядок совпадает с порядком входа.
// С 05.05 бэкенд фильтрует полные дубли (itemId+supplier+price+note) и
// возвращает счётчик пропущенных — нужно при ручном импорте Excel менеджером.
export async function createProcurementResultsBulk(
  items: Array<{
    requestItemId: string
    supplierName: string
    price: number
    currency?: string
    stockQty?: number
    leadTimeDays?: number
    moq?: number
    dateCode?: string
    conditionType?: string
    sourceType: SourceType
    note?: string
  }>,
): Promise<{ created: ProcurementResult[]; skippedDuplicates: number; skippedOrphans?: number }> {
  const res = await apiClient.post<{
    success: true
    data: { created: ProcurementResult[]; skippedDuplicates: number; skippedOrphans?: number }
  }>(`/procurement-results/bulk`, { items })
  return res.data.data
}
